<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, getCurrentInstance, ref, watch } from 'vue'
import _ from 'lodash'
import { track, EVENTS } from '@/lib/tracking'
import StreamService from '@/services/StreamService'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import Badge from '../../Badge.vue'
import FormButton from '../../Atoms/FormButton.vue'
import FormInput from '../../Atoms/FormInput.vue'
import TabGroup from '../../Atoms/TabGroup.vue'
import TabItem from '../../Atoms/TabItem.vue'
import FormRadio from '../../Atoms/FormRadio.vue'
import Tooltip from '../../Atoms/Tooltip.vue'
import IconBase from '../../icon/IconBase.vue'
import IconCopy from '../../icon/IconCopy.vue'
import IconLinkExternal from '../../icon/IconLinkExternal.vue'
import IconInfo from '../../icon/IconInfo.vue'
import IconInfoCircleOutline from '../../icon/IconInfoCircleOutline.vue'
import ButtonCopyToClipboard from '../../Moleculas/ButtonCopyToClipboard.vue'
import Alert from '../../Atoms/Alert.vue'
import FormSwitch from '../../Atoms/FormSwitch.vue'
import StreamSidebarPlaybackPlaceholder from './StreamSidebarPlaybackPlaceholder.vue'
import ConfirmModal from '@/components/modals/ConfirmModal';
import EmbedCodeWidget from '@/components/Moleculas/EmbedCodeWidget.vue';

const props = defineProps({
  stream: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  streamAlive: {
    type: Boolean
  },
  selectedPreviewTab: {
    type: String
  },
  streamSourceType: {
    type: String
  },
  hlsUrl: {
    type: String
  },
  llHls: {
    type: Boolean,
    default: false
  },
  sourceTypes: {
    type: Object
  },
  failoverIngestToggleState: {
    type: Boolean,
    default: false,
  },
  isRtmpPullEnabled: {
    type: Boolean,
    default: false
  },
  rmptPullUrlProcessing: {
    type: Boolean,
    default: false
  },
  selectedLinkedSource: {
    type: Object,
    default: null
  },
  streamKeySrt: {
    type: String,
    default: null
  },
  sourceSet: {
    type: Boolean,
    default: false
  },
  failoverIgestActive: {
    type: Boolean,
    default: false
  },
  isPasswordEnabled: {
    type: Boolean,
    default: false
  },
  isAnyBackupStreamLive: {
    type: Boolean,
    default: false
  },
  srtRegionIngestDestination: {
    type: String,
    default: null
  },
  rtmpPullUrl: {
    type: String,
    default: ''
  },
  srtPullUrl: {
    type: String,
    default: ''
  },
})

const instance = getCurrentInstance()
const root = instance.proxy.$root
const { notify } = useNotification()
const store = useStore()

const emit = defineEmits(['stream-updated', 'toggle-stream', 'refresh-stream'])

const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])
const maxSubSecondStreamViewers = computed(() => store.getters['User/maxSubSecondStreamViewers'])

const selectedPlaybackTab = ref('full')
const iframe = ref('responsive')
const llHlsRef = ref(false)
const enablePlayback = ref(true)

watch(() => props.llHls, (v) => {
  llHlsRef.value = Boolean(v)
}, {
  immediate: true
})

// Initialize enablePlayback from stream metadata
watch(() => props.stream.metadata?.enablePlayback, (newValue) => {
  if (newValue !== undefined && newValue !== null) {
    enablePlayback.value = newValue
  } else if(_.isNil(enablePlayback.value)) {
    enablePlayback.value = true;
  }
}, {
  immediate: true
})

const saveEnablePlayback = async () => {
   if (!enablePlayback.value) { 
      showModal('confirm-toggling-recording-off')
   } else {
      try {
        await StreamService.saveStreamMetadata(props.stream._id, 'enablePlayback', enablePlayback.value);

        const updatedMetadata = {
          ...props.stream.metadata,
          enablePlayback: enablePlayback.value
        };

        emit('stream-updated', {
          metadata: updatedMetadata
        });
        await StreamService.clearStreamMetadataCache(props.stream._id, props.stream.key);
        notify({ group: 'success', text: 'Playback setting saved' });
      } catch (e) {
        enablePlayback.value = !enablePlayback.value;
        notify({ group: 'error', text: 'Could not save playback setting' });
      }

   }
}

const selectPlaybackTab = (tabName) => selectedPlaybackTab.value = tabName

// Anti-Piracy (DRM) protection requires the player iframe to be granted the
// `encrypted-media` permission so EME/DRM playback can initialize.
const drmProtectionEnabled = computed(() => !!props.stream.metadata?.drmProtection)

const iframeAllowAttr = computed(() =>
  drmProtectionEnabled.value ? 'autoplay; encrypted-media' : 'autoplay'
)

const streamIframeCode = computed(() => {
  const embedUrl = streamEmbedUrl.value;
  const allow = iframeAllowAttr.value;
  let htmlCode = "";
  if (iframe.value === "responsive") {
    htmlCode = `<iframe src="${embedUrl}" width="100%" style="aspect-ratio: 16/9; min-height: 340px;" frameborder="0" scrolling="no" allow="${allow}" allowfullscreen  webkitallowfullscreen mozallowfullscreen oallowfullscreen msallowfullscreen></iframe>`
  } else {
    htmlCode = `<iframe src="${embedUrl}" width="590" height="431" frameborder="0" scrolling="no" allow="${allow}" allowfullscreen webkitallowfullscreen mozallowfullscreen oallowfullscreen msallowfullscreen></iframe>`
  }
  if (selectedPlaybackTab.value === 'audio') {
    htmlCode = `<iframe src="${embedUrl}" width="100%" style="height: 50px;" frameborder="0" scrolling="no" allow="${allow}" allowfullscreen webkitallowfullscreen mozallowfullscreen oallowfullscreen msallowfullscreen></iframe>`
  }

  return htmlCode;
})

const streamEmbedUrl = computed(() => {
  let embedUrl = `${process.env.VUE_APP_PLAYER_APP_BASE_URL}/${props.stream.key}`;

  if (selectedPlaybackTab.value === 'audio') {
    // if stream source is webcam for only audio we need point player to 
    // pick correct track audio number(#2) from audio source
    let webcamSrc = '';
    if (props.stream.type === 'live' && props.streamSourceType === 'webcam') {
      webcamSrc = '&webcam=true';
    }
    let onlyAudio = `?onlyAudio=true${webcamSrc}`;
    if (embedUrl.includes('?')) {
      onlyAudio = `&onlyAudio=true${webcamSrc}`;
    }

    embedUrl += onlyAudio;
  }
  if (selectedPlaybackTab.value === 'll-hls') {
    let ll = `?ll=true`;
    if (embedUrl.includes('?')) {
      ll = `&ll=true`;
    }

    embedUrl += ll;
  }
  if (selectedPlaybackTab.value === 'ull') {
    embedUrl += '/realtime';
  }
  return embedUrl;
})

const clipboardCopy = async (value) => {
  await navigator.clipboard.writeText(value);
  notify({ group: 'info', text: 'Copied to clipboard' });
}

const baseCharge = computed(() => {
  const subscription = addonSubscriptions.value.find(sub => sub.category === 'live')
  return subscription?.package?.baseCharge 
})

const allowHls = computed(() => {
  const subscription = addonSubscriptions.value.find(sub => sub.category === 'live')
  const hlsEnabled = _.get(subscription, 'definitionOverride.hlsEnabled', _.get(subscription, 'package.definition.hlsEnabled', false));
  if (hlsEnabled) return true;
  if ((baseCharge.value > 750 && subscription?.package?.cycleAge >= 365) || (baseCharge.value > 100 && subscription?.package?.cycleAge < 365)) {
    return true
  } else return false
})

const linkedCpCode = computed(() => {
  const subscription = addonSubscriptions.value.find(sub => sub.category === 'live')
  const cpcode = _.get(subscription, 'definitionOverride.cdnDeploymentId');
  return cpcode;
})

const labelHls = computed(() => {
  if (props.hlsUrl && allowHls.value && linkedCpCode.value) return `HLS URL`

  if (allowHls.value && !linkedCpCode.value) return `HLS URL is available. <router-link class="text-surface-8 underline" onclick="Intercom('show')">Contact us</router-link> to activate.`

  return `HLS URL. <router-link class="text-surface-8 underline" to="/subscribe">Upgrade</router-link> to access this feature.`
})

const shouldShowInputs = computed(() => {
  if (selectedPlaybackTab.value === 'll-hls' && llHlsRef.value) return true
  if (selectedPlaybackTab.value === 'audio') return true
  if (selectedPlaybackTab.value === 'full') return true
  return false
})

const hasLLHls = computed(() => {
  // TODO(remove): hardcode access to LL hls feature for user with premuin sub
  // LL hls should be blocked for current sub for all other clients
  if (props.stream.user === '653686e34d8f1722c0b3c9a4') {
    return true
  }
  if (props.stream.user === '66e83ba5bb1df9597da804a9') {
    return true
  }
  return store.state.User.aggregatedDefinition.live.llHls ?? false
})

const saveLLHls = async () => {
  try {
    await StreamService.saveStreamMetadata(props.stream._id, 'llHls', llHlsRef.value);
    const updatedMetadata = {
      ...(props.stream.metadata || {}),
      llHls: llHlsRef.value
    }
    emit('stream-updated', {
      metadata: updatedMetadata
    })
    await StreamService.clearStreamMetadataCache(props.stream._id, props.stream.key)
    notify({ group: 'success', text: 'Setting saved' })
  } catch (e) {
    llHlsRef.value = !llHlsRef.value;
    notify({ group: 'error', text: 'Unable to save your changes.' });
  }
}

const toggleCancel = () => {
  enablePlayback.value = !enablePlayback.value;
}

const toggleOffRecording = async () => {
  try {
    await StreamService.saveStreamMetadata(props.stream._id, 'enablePlayback', enablePlayback.value);

    const updatedMetadata = {
      ...props.stream.metadata,
      enablePlayback: enablePlayback.value
    };

    emit('stream-updated', {
      metadata: updatedMetadata
    });
    await StreamService.clearStreamMetadataCache(props.stream._id, props.stream.key);
    notify({ group: 'success', text: 'Playback setting saved' });
  } catch (e) {
    enablePlayback.value = !enablePlayback.value;
    notify({ group: 'error', text: 'Could not save playback setting' });
  }
}

const shouldShowHlsUrl = computed(() => selectedPlaybackTab.value === 'full' && (props.stream.type !== 'subsecond'))

const trackPlayerUrlCopy = () => {
  track(EVENTS.CONTENT_URL_COPIED, {
    streamId: props.stream._id,
    streamType: props.stream.type,
    contentType: 'live',
    copyType: 'player_url',
  })
}

const copyPlayerUrl = () => {
  clipboardCopy(streamEmbedUrl.value)
  trackPlayerUrlCopy()
}

const trackHlsUrlCopy = () => {
  if (!(allowHls.value && linkedCpCode.value)) return
  track(EVENTS.CONTENT_URL_COPIED, {
    streamId: props.stream._id,
    streamType: props.stream.type,
    contentType: 'live',
    copyType: 'hls_url',
  })
}

const copyHlsUrl = () => {
  const value = allowHls.value && linkedCpCode.value ? props.hlsUrl : 'xxxxxxxx'
  clipboardCopy(value)
  trackHlsUrlCopy()
}

// EmbedCodeWidget/ButtonCopyToClipboard are shared and can't be modified, so we
// listen for the copy-button click bubbling up from the embed code area.
const onEmbedCodeCopy = (e) => {
  if (!e.target.closest('button')) return
  track(EVENTS.EMBED_CODE_COPIED, {
    streamId: props.stream._id,
    streamType: props.stream.type,
    contentType: 'live',
    copyType: 'embed_iframe',
    drmProtection: props.stream.metadata?.drmProtection,
  })
}
</script>

<template>
  <ConfirmModal :modal-id="'confirm-toggling-recording-off'" message="Disable Player?" body="Disabling the player will put it in an offline state and make it unavailable for viewers. Are you sure you want to continue?"
    ok-text="Confirm" cancel-text="Cancel" @modal-cancel="toggleCancel" @modal-confirm="toggleOffRecording()" />
  <section class="p-3 bg-surface-2 rounded-b-lg" v-if="stream.type != 'restream'">
    <!-- Enable Playback Toggle -->
    <div class="flex items-center justify-between mb-3" v-if="stream.type === 'live'">
      <div class="flex items-center gap-2">
        <p class="mb-0 font-medium">Enable Playback</p>
        <Tooltip v-if="!enablePlayback" content="The player is currently offline and unavailable for playback.">
          <icon-base :width="13" :height="13" :width-view-box="16" :height-view-box="16" class="text-[#AEBAD6] shrink-0">
            <icon-info />
          </icon-base>
        </Tooltip>
      </div>
      <FormSwitch v-model="enablePlayback" @change="saveEnablePlayback" />
    </div>

     <!-- Playback Content - Only shown when enabled -->
    <div v-if="enablePlayback || stream.type != 'live'">
    <Alert v-if="stream.paywallPopupAuth" show type="warning" class="flex p-2 mb-1">
      This player is now behind a paywall.
    </Alert>
    <Alert v-if="!isPasswordEnabled && stream.type === 'live'" class="inline-flex items-center w-full" show
      type="warning2">
      Ingest is not secure.
    </Alert>
    <TabGroup type="segmented">
      <TabItem :is-active="selectedPlaybackTab === 'full'" @click="selectPlaybackTab('full')">Normal</TabItem>
      <TabItem v-if="stream.type !== 'subsecond'" :is-active="selectedPlaybackTab === 'll-hls'" @click="selectPlaybackTab('ll-hls')">LL</TabItem>
      <TabItem v-if="stream.type !== 'subsecond'" :is-active="selectedPlaybackTab === 'ull'" @click="selectPlaybackTab('ull')">ULL</TabItem>
      <TabItem :is-active="selectedPlaybackTab === 'audio'" @click="selectPlaybackTab('audio')">Audio</TabItem>
    </TabGroup>

    <div v-if="selectedPlaybackTab === 'ull'" class="mt-3">
      <p class="text-surface-9 text-sm mb-3">Ultra Low Latency is now Sub Second Streaming.</p>
      <FormButton size="md" to="/sub-second" class="mb-3">
        Create Sub Second Streaming
      </FormButton>
    </div>
    <Alert v-if="selectedPlaybackTab === 'll-hls'" hide-icon class="mt-3">
      Low Latency Embed will give you less than 15 seconds of latency.
    </Alert>
    <Alert v-if="selectedPlaybackTab === 'audio'" class="mt-3">
      The below players will have only audio from your Livestream.
    </Alert>
    <div class="mt-3" v-if="selectedPlaybackTab === 'll-hls'">
      <Tooltip :active="!hasLLHls">
        <FormSwitch class="w-full justify-between" labelPosition="left" label="Enable Low Latency"  :disabled="!hasLLHls" @change="saveLLHls" v-model="llHlsRef" />
          <template #content>
            Your plan is not supported. <a target="_blank" class="underline" href="/app/subscribe">Upgrade</a> to access this feature
          </template>
      </Tooltip>
    </div>
    <div v-if="shouldShowInputs || shouldShowHlsUrl" class="mt-3 bg-surface-1 rounded-lg p-3 flex flex-col gap-y-3">
      <div v-if="shouldShowInputs" @click="onEmbedCodeCopy">
        <EmbedCodeWidget :url="streamEmbedUrl" :disabled="!streamEmbedUrl" :encrypted-media="drmProtectionEnabled" v-model="iframe" />
      </div>
      <div v-if="shouldShowInputs">
        <label class="block mb-1 text-sm text-surface-8">Direct Player Link</label>
        <div class="flex relative w-full">
          <FormInput customClasses="!pr-16" :modelValue="streamEmbedUrl" @click="copyPlayerUrl" readonly />
          <ButtonCopyToClipboard class="absolute right-6 top-px z-10" :text="streamEmbedUrl" @click="trackPlayerUrlCopy" />
          <FormButton class="absolute right-0 top-px z-10" isLink isIcon type="link" size="sm" :href="streamEmbedUrl">
            <icon-base class="" fill="none">
              <icon-link-external />
            </icon-base>
          </FormButton>
        </div>
      </div>
      <div v-if="shouldShowHlsUrl">
        <label class="block mb-1 text-sm text-surface-8" v-html="labelHls" />
        <div class="flex relative w-full">
          <FormInput customClasses="!pr-8" :modelValue="allowHls && linkedCpCode ? hlsUrl : 'xxxxxxxx'"
            :disabled="!allowHls || !linkedCpCode" @click="copyHlsUrl"
            readonly />
          <ButtonCopyToClipboard v-if="allowHls && linkedCpCode" class="absolute right-0 top-px z-10" :text="hlsUrl" @click="trackHlsUrlCopy" />
        </div>
      </div>
    </div>
    </div>

    <!-- Pull URLs (output) — independent of the player toggle above -->
    <div v-if="stream.type === 'live'" class="mt-4 bg-surface-1 rounded-lg p-3 flex flex-col gap-y-3">
      <div class="flex items-center gap-1.5">
        <h4 class="text-sm font-medium text-surface-9 mb-0">Pull this stream</h4>
        <Tooltip content="Pull the live stream out of Castr as an RTMP or SRT source. Available while the stream is live.">
          <icon-base fill="none" width="13" height="13" width-view-box="24" height-view-box="24" class="text-surface-6">
            <icon-info-circle-outline />
          </icon-base>
        </Tooltip>
      </div>
      <div>
        <label class="block mb-1 text-sm text-surface-8">RTMP pull URL</label>
        <div v-if="isRtmpPullEnabled && rtmpPullUrl" class="flex relative w-full">
          <FormInput customClasses="!pr-8" :modelValue="rtmpPullUrl" @click="clipboardCopy(rtmpPullUrl)" readonly />
          <ButtonCopyToClipboard class="absolute right-0 top-px z-10" :text="rtmpPullUrl" />
        </div>
        <FormInput
          v-else
          customClasses="!pr-8"
          :modelValue="isRtmpPullEnabled ? 'Available when the stream is live' : 'Not available in your current plan'"
          disabled
          readonly
        />
      </div>
      <div>
        <label class="block mb-1 text-sm text-surface-8">SRT pull URL</label>
        <div v-if="isRtmpPullEnabled && srtPullUrl" class="flex relative w-full">
          <FormInput customClasses="!pr-8" :modelValue="srtPullUrl" @click="clipboardCopy(srtPullUrl)" readonly />
          <ButtonCopyToClipboard class="absolute right-0 top-px z-10" :text="srtPullUrl" />
        </div>
        <FormInput
          v-else
          customClasses="!pr-8"
          :modelValue="isRtmpPullEnabled ? 'Available when the stream is live' : 'Not available in your current plan'"
          disabled
          readonly
        />
      </div>
      <p class="text-tiny text-surface-6 mb-0 leading-snug">
        Pull addresses can change as streams are load-balanced. For a constant URL, follow the
        <a href="https://docs.castr.com/en/articles/4851668-how-to-get-constant-rtmp-address-in-castr" target="_blank"
          class="text-surface-7 underline hover:text-white">RTMP</a>
        or
        <a href="https://docs.castr.com/en/articles/6788134-how-to-get-constant-srt-address-in-castr" target="_blank"
          class="text-surface-7 underline hover:text-white">SRT</a>
        guide.
      </p>
    </div>

    <p v-if="stream.type === 'subsecond'" class="text-surface-7 text-sm mt-3">Maximum {{ maxSubSecondStreamViewers }} concurrent {{ maxSubSecondStreamViewers === 1 ? 'viewer' : 'viewers' }}. Need more? <FormButton type="link" customClasses="underline" onclick="Intercom('showNewMessage', 'I want to talk about low latency solution')">Contact us</FormButton></p>
  </section>
  <StreamSidebarPlaybackPlaceholder v-else />
</template>